import { type RefObject, useEffect, useRef } from 'react';
import type { useServerData } from '../hooks/useServerData';
import type { EagGridStore } from '../store/createEagGridStore';
import type { FeGridSettings } from '../types/Api';
import type { Filters } from '../types/Filters';
import { REFETCH_EAGRID_EVENT, type RefetchEagridCustomEvent } from '../utils/refreshDatagrid';
import { clearPageDataCacheForGrid } from './pageDataCache';
type UseGridDataFetchingParams = {
  gridCode: string;
  store: EagGridStore;
  serverData: ReturnType<typeof useServerData>;
  offset: number;
  filterValues: Filters;
  sortModelKey: string;
  gridSettings: FeGridSettings;
  queryId: string | undefined;
  refreshRef: RefObject<(() => void) | null>;
};
type UseGridDataFetchingReturn = {
  refreshData: () => void;
  serverDataRef: RefObject<ReturnType<typeof useServerData>>;
  offsetRef: RefObject<number>;
  queryIdRef: RefObject<string | undefined>;
};

/**
 * Owns the data fetching lifecycle: initial fetch, pagination, filter/sort triggers,
 * external refresh events, and the imperative refreshRef for parent access.
 */
export const useGridDataFetching = (params: UseGridDataFetchingParams): UseGridDataFetchingReturn => {
  const serverDataRef = useRef(params.serverData);
  const offsetRef = useRef(params.offset);
  const queryIdRef = useRef(params.queryId);

  // eslint-disable-next-line no-restricted-syntax -- intentional: no-deps useEffect syncs refs every render
  useEffect(() => {
    serverDataRef.current = params.serverData;
    offsetRef.current = params.offset;
    queryIdRef.current = params.queryId;
  });

  // Fetch effect dependencies are TRIGGERS that tell React when to refetch.
  // The actual values (filters, sort, settings) are read from store.getState() inside
  // fetchPage at call time — this eliminates stale-closure bugs from the previous debounce.
  // sortModelKey prevents spurious refetches on column reorder/resize (which change
  // columnStates order but not the actual sort). gridSettings is included so itemsPerPage
  // changes on page 0 (offset stays 0) still trigger a refetch with the new page size.
  // eslint-disable-next-line no-restricted-syntax -- intentional: reactive data fetch; 4 independent sources (pagination, filter change, sort click, preset switch) can each trigger refetch; cannot be consolidated into one event handler
  useEffect(() => {
    serverDataRef.current.fetchPage(params.offset);
  }, [params.offset, params.filterValues, params.sortModelKey, params.gridSettings]);

  // eslint-disable-next-line no-restricted-syntax -- intentional: no-deps useEffect syncs refreshRef every render
  useEffect(() => {
    // eslint-disable-next-line react-hooks/immutability -- props.refreshRef is a MutableRefObject intentionally passed for write access; writing .current is the expected usage
    params.refreshRef.current = () => {
      clearPageDataCacheForGrid(params.gridCode);
      serverDataRef.current.fetchPage(offsetRef.current);
    };
  });

  // Listen for refresh events dispatched via refreshDatagrid(gridCode) utility
  // eslint-disable-next-line no-restricted-syntax -- intentional: window.addEventListener with cleanup; subscribes to REFETCH_EAGRID_EVENT dispatched by refreshDatagrid() utility from outside the component tree
  useEffect(() => {
    const handleRefetchEvent = (e: Event) => {
      const customEvent = e as CustomEvent<RefetchEagridCustomEvent>;
      if (customEvent.detail.gridCode !== params.gridCode) {
        return;
      }
      params.store.clearPromiseLocks(); // clear stale promise locks on refresh
      clearPageDataCacheForGrid(params.gridCode);
      serverDataRef.current.fetchPage(offsetRef.current);
      if (customEvent.detail.options?.deselectAll) {
        params.store.deselectAll();
      }
    };
    window.addEventListener(REFETCH_EAGRID_EVENT, handleRefetchEvent);
    return () => window.removeEventListener(REFETCH_EAGRID_EVENT, handleRefetchEvent);
  }, [params.gridCode, params.store]);
  const refreshData = () => {
    clearPageDataCacheForGrid(params.gridCode);
    serverDataRef.current.fetchPage(offsetRef.current);
  };
  return {
    refreshData,
    serverDataRef,
    offsetRef,
    queryIdRef
  };
};